import { useCallback, useEffect, useRef, useState, memo } from 'react'
import {
  ActivityIndicator,
  ListRenderItem,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from 'react-native'
import { useTheme } from 'styled-components/native'
import { FlatList } from 'react-native-gesture-handler'
import moment from 'moment'

import { useDispatch, useSelector } from 'APP/Hooks/redux'
import PatientHistoryActions from 'APP/Redux/PatientHistoryRedux'
import { memberTasks } from 'APP/Store/MemberTasks'
import { ChatPost, ChatUser, useChatContext } from 'APP/Lib/Context'
import Post from 'APP/Components/Chat/Post'
import I18n from 'APP/I18n'
import InputBar from 'APP/Components/Chat/InputBar'
import { PostContext } from 'APP/Lib/Context'
import Notices from 'APP/Components/Chat/Notices'
import { TypingIndicator } from 'APP/Converse/Chat/TypingIndicator'
import AppointmentBanner from 'APP/Components/AppointmentBooking/AppointmentBanner'
import { MemberTaskBarEpisode } from 'APP/Components/MemberTaskBar'
import { formatTimeForAppointment } from 'APP/Containers/ChatScreen/helper'
import { useOnLayout } from 'APP/Hooks/Layout'
import { MappedEnrichedEncounter } from 'APP/Services/Coredata/index.types'
import type { NormalizedAppointment } from 'APP/Store/History/types'
import {
  ConversationContainer,
  InputBarContainer,
  LoadingContainer,
  LoadingText,
  ScrollViewContainer,
  TopBannersContainer,
  UnreadMessagesButton,
  UnreadMessagesContainer,
} from './style'

const maintainVisibleContentPosition = {
  minIndexForVisible: 0,
  autoscrollToTopThreshold: 0,
}

const TIME_GAP_TO_SHOW_TIMESTAMP = 1000 * 60 * 10 // 10 minutes

type PostProviderProps = {
  item: ChatPost
  users: ChatUser[]
  order: string[]
  index: number
  orderedPosts: ChatPost[]
  encounters: Record<string, MappedEnrichedEncounter>
}

const PostProvider = memo(function PostProvider({
  item,
  users,
  order,
  index,
  orderedPosts,
  encounters,
}: PostProviderProps) {
  const currentUserId = item.user_id
  const isMember = currentUserId === users[0].id
  const isLastPost = order[0] === item.id
  const postUser = users.find((u) => u.id === item.user_id)
  const previousPost = orderedPosts?.[index + 1]
  const previousUserId = previousPost?.user_id
  const nextPost = orderedPosts?.[index - 1]
  const nextUserId = nextPost?.user_id
  const previousPostIsSameUser = previousUserId === currentUserId
  const nextPostIsSameUser = nextUserId === currentUserId
  const encounter = encounters?.[item?.actionContext?.encounterId || '']
  const nextPostSameTimestamp =
    (nextPost?.create_at || 0) - (item?.create_at || 0) < TIME_GAP_TO_SHOW_TIMESTAMP

  const previousPostIsSameDay = previousPost
    ? moment(item.create_at).isSame(previousPost?.create_at, 'day')
    : false
  const nextPostIsSameDay = nextPost
    ? moment(item.create_at).isSame(nextPost?.create_at, 'day')
    : false
  return (
    <PostContext.Provider
      key={item.id}
      value={{
        post: item,
        postUser,
        isMember,
        previousPostIsSameUser,
        nextPostIsSameUser,
        previousPostIsSameDay,
        nextPostIsSameDay,
        encounter,
        isLastPost,
        nextPostSameTimestamp,
      }}
    >
      <Post />
    </PostContext.Provider>
  )
})

type ConversationProps = {
  upcomingAppointment: NormalizedAppointment
  onBannerCtaPress: () => void
}

const Conversation = ({ upcomingAppointment, onBannerCtaPress }: ConversationProps) => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const { order, orderedPosts, users, conversation, channelId, lastPost, userId } = useChatContext()

  const encounters = useSelector((state) => state.history.encounters)
  const scrollViewRef = useRef<FlatList>(null)
  const { layout: inputBarLayout, onLayout: onInputBarLayoutChanged } = useOnLayout()

  const [isAtBottom, setIsAtBottom] = useState(true)
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false)

  const prevLastPost = useRef<ChatPost>(undefined)

  // Fetch this episode's member tasks on entry (the only place we fetch tasks).
  useEffect(() => {
    if (channelId) {
      dispatch(memberTasks.fetchTasks({ scope: 'episode', episodeId: channelId }))
    }
  }, [dispatch, channelId])

  useEffect(() => {
    const receivedNewPost = prevLastPost.current && lastPost?.id !== prevLastPost.current?.id
    const lastPostIsFromUser = lastPost?.user_id === userId
    if (receivedNewPost && !isAtBottom && !lastPostIsFromUser) {
      setHasUnreadMessages(true)
    }
    if (receivedNewPost && isAtBottom) {
      setTimeout(() => scrollToBottom(), 0)
    }
    prevLastPost.current = lastPost
  }, [isAtBottom, lastPost, userId])

  const checkIsAtBottom = useCallback(
    (scrollOffset: number) => {
      const newIsAtBottom = scrollOffset < inputBarLayout.height
      if (isAtBottom !== newIsAtBottom) {
        if (newIsAtBottom) {
          setHasUnreadMessages(false)
        }
        setIsAtBottom(newIsAtBottom)
      }
    },
    [isAtBottom, setIsAtBottom, setHasUnreadMessages, inputBarLayout.height]
  )

  const onScroll = useCallback(
    ({ nativeEvent }: NativeSyntheticEvent<NativeScrollEvent>) => {
      // TODO [DIA-88276] [MA] Scroll bug in chat window -- checkIsAtBottom call is faulty

      const { contentOffset } = nativeEvent
      checkIsAtBottom(contentOffset.y)
    },
    [checkIsAtBottom]
  )

  const scrollToBottom = (animated = true) => {
    setIsAtBottom(true)
    setHasUnreadMessages(false)
    scrollViewRef?.current?.scrollToOffset({ offset: 0, animated })
  }

  const onReachedEndOfChat = useCallback(() => {
    if (!conversation || !channelId) return

    if (!conversation.fetchingPosts && !conversation.allOlderPostsFetched) {
      dispatch(PatientHistoryActions.fetchOlderPostsForChannel(channelId))
    }
  }, [conversation, dispatch, channelId])

  const renderPost: ListRenderItem<ChatPost> = useCallback(
    ({ item, index }) => (
      <PostProvider
        item={item}
        index={index}
        users={users}
        order={order}
        orderedPosts={orderedPosts}
        encounters={encounters}
      />
    ),
    [users, order, orderedPosts, encounters]
  )

  return (
    <ConversationContainer inputBarHeight={inputBarLayout.height}>
      <InputBarContainer onLayout={onInputBarLayoutChanged}>
        <InputBar scrollToBottom={scrollToBottom} />
      </InputBarContainer>
      <ScrollViewContainer
        testID="CONVERSATION"
        ref={scrollViewRef}
        data={orderedPosts}
        renderItem={renderPost}
        keyExtractor={(item) => item.id}
        inverted
        onScroll={onScroll}
        onEndReached={onReachedEndOfChat}
        onEndReachedThreshold={0.5}
        maintainVisibleContentPosition={maintainVisibleContentPosition}
        ListHeaderComponent={TypingIndicator}
      />
      <TopBannersContainer>
        {upcomingAppointment ? (
          <AppointmentBanner
            title={I18n.t('ConversationScreen.appointmentBanner.title')}
            time={formatTimeForAppointment(upcomingAppointment?.start_at)}
            onCtaPress={onBannerCtaPress}
            ctaLabel={I18n.t('ConversationScreen.appointmentBanner.cta')?.toUpperCase()}
          />
        ) : (
          <Notices channelId={channelId} conversation={conversation} />
        )}
        <MemberTaskBarEpisode episodeId={channelId} />
      </TopBannersContainer>
      {conversation?.fetchingPosts ? (
        <LoadingContainer>
          <ActivityIndicator color={theme.colors.text} testID="SPINNER" />
          <LoadingText>{I18n.t('ConversationScreen.loading')}</LoadingText>
        </LoadingContainer>
      ) : null}
      {hasUnreadMessages && (
        <UnreadMessagesContainer>
          <UnreadMessagesButton
            onPress={scrollToBottom}
            title={I18n.t('ConversationScreen.unreadMessages')}
            icon="arrow-downward"
            iconVariant="materialIcons"
            variant="primary"
            small
            widthVariant="content"
          />
        </UnreadMessagesContainer>
      )}
    </ConversationContainer>
  )
}

export default Conversation
